import React, { useState, useEffect } from 'react';
import { apiFetch } from './utils/api';

function SettingsPPMDiscountTiers() {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    fetchTiers();
  }, []);

  const fetchTiers = async () => {
    try {
      const response = await apiFetch('/api/ppm/contracts/discount-tiers');
      const data = await response.json();
      
      // Ensure all standard terms exist
      const standardTerms = [1, 2, 3, 5];
      const tiersMap = {};
      data.forEach(tier => {
        tiersMap[tier.term_years] = tier.discount_percent;
      });
      
      const allTiers = standardTerms.map(term => ({
        term_years: term,
        discount_percent: tiersMap[term] || 0
      }));
      
      setTiers(allTiers);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching discount tiers:', err);
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);

    try {
      // Update each tier
      for (const tier of tiers) {
        await apiFetch(`/api/ppm/contracts/discount-tiers/${tier.term_years}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ discount_percent: tier.discount_percent })
        });
      }

      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('Error saving tiers:', err);
      alert('Failed to save discount tiers: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const updateTier = (termYears, newDiscount) => {
    setTiers(tiers.map(t => 
      t.term_years === termYears 
        ? { ...t, discount_percent: parseFloat(newDiscount) || 0 } 
        : t
    ));
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-gray-600">Loading discount tiers...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-3xl">
        {/* Header */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            PPM Discount Tiers
          </h2>
          <p className="text-gray-600">
            Configure automatic discount suggestions for multi-year PPM contracts.
            These discounts are automatically suggested when creating contracts but can be overridden.
          </p>
        </div>

        {/* Discount Tiers */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6">
            <div className="space-y-4">
              {tiers.map(tier => (
                <div key={tier.term_years} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">
                      {tier.term_years} {tier.term_years === 1 ? 'Year' : 'Years'}
                    </div>
                    <div className="text-sm text-gray-500">
                      Suggested discount for {tier.term_years}-year contracts
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <input
                      type="number"
                      step="0.5"
                      min="0"
                      max="100"
                      value={tier.discount_percent}
                      onChange={(e) => updateTier(tier.term_years, e.target.value)}
                      className="w-24 px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-rose-500 focus:border-transparent text-right"
                    />
                    <span className="text-gray-700 font-medium">%</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Example Breakdown */}
            <div className="mt-6 p-4 bg-rose-50 border border-rose-200 rounded-lg">
              <h3 className="font-semibold text-rose-900 mb-3 text-sm">Example</h3>
              <div className="text-sm text-gray-700 space-y-1">
                <p>Annual contract value: £5,000</p>
                <p>3-year contract with {tiers.find(t => t.term_years === 3)?.discount_percent || 0}% discount:</p>
                <p className="font-semibold text-rose-700 mt-2">
                  Total: £{(5000 * 3 * (1 - (tiers.find(t => t.term_years === 3)?.discount_percent || 0) / 100)).toFixed(2)}
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="px-6 py-4 bg-gray-50 border-t flex items-center justify-between">
            <div className="text-sm text-gray-500">
              Changes apply to new contracts only
            </div>
            <div className="flex items-center space-x-3">
              {saved && (
                <span className="text-sm text-green-600 font-medium">✓ Saved</span>
              )}
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-rose-600 text-white rounded font-medium hover:bg-rose-700 disabled:opacity-50 transition"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>

        {/* Additional Info */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="font-semibold text-blue-900 mb-2 text-sm">💡 How It Works</h3>
          <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
            <li>When creating a contract, selecting a term auto-fills the suggested discount</li>
            <li>Users can override the suggested discount on any contract</li>
            <li>Total contract value = Annual Value × Years × (1 - Discount %)</li>
            <li>Longer terms encourage customer commitment with better pricing</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default SettingsPPMDiscountTiers;
